import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import BaseFilter from './BaseFilter';
import { getJwtToken, getRefreshToken, setTokens, getActualLocalizationString, tlAddMinutes, tlIsBefore, logoutCurrentUser } from "../../Infrastructure/CorBase/CoreBase";

export default abstract class BaseService {
	protected _resourceName = '';
	protected timeOutSet = 1200000;

	constructor(resourceName: string) {
		this._resourceName = resourceName
	}

	public getAll() {
		return this.getBase(this._resourceName);
	}

	public getAllWithResourceName(resourceName: string) {
		return this.getBase(`${this._resourceName + '/' + resourceName}`);
	}

	public getById(id: number) {
		return this.getBase(`${this._resourceName}/${id}`, { timeout: this.timeOutSet });
	}

	protected getByIdWithResourceName(id: number, resourceName: string) {
		return this.getBase(`${resourceName}/${id}`);
	}

	public create(data: any) {
		return this.createBase(this._resourceName, data, { timeout: this.timeOutSet });
	}

	public update(id: number, data: any) {
		return this.updateBase(`${this._resourceName}/${id}`, data, { timeout: this.timeOutSet });
	}

	public updateWithResourceName(id: number, resourceName: string, data: any) {
		return this.updateBase(`${this._resourceName + '/' + resourceName}/${id}`, data, { timeout: this.timeOutSet });
	}

	public delete(id: number) {
		return this.deleteBase(`${this._resourceName}/${id}`, { timeout: this.timeOutSet });
	}

	public deleteListWithResourceName(id: number[], resourceName: string) {
		let ids: string = id.join(','); 
		return this.deleteBase(`${this._resourceName + '/' + resourceName}`, { params: { Ids: ids }, timeout: this.timeOutSet });
	}

	public updateListWithResourceName(id: number[], resourceName: string) {
		let ids: string = id.join(',');
		return this.updateBase(`${this._resourceName + '/' + resourceName}`, { Ids: ids });
	}

	protected getAllByFilterBase(filter: BaseFilter, resourceName: string = '') {
		return this.getBase(this._resourceName + '/' + resourceName, { params: filter.getAllParams(), timeout: this.timeOutSet });
	}

	protected getFileByFilter(filter: BaseFilter, resourceName: string = '') {
		return this.getFileBase(this._resourceName + '/' + resourceName, { params: filter.getAllParams(), timeout: this.timeOutSet  })
	}

	protected getFileByIdWithResourceName(id: number, resourceName: string) {
		return this.getFileBase(`${resourceName}/${id}`);
	}

	protected createBase(url: string, data: any, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		return this.createBaseFromUrl(url, data, config).catch((error) => {
			return this.renewTokenIfIsExpiredOrLogout(error.response)
				.then(() => this.createBaseFromUrl(url, data, config));
		});
	}

	protected deleteBase(url: string, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		return this.deleteBaseFromUrl(url, config).catch((error) => {
			return this.renewTokenIfIsExpiredOrLogout(error.response)
				.then(() => this.deleteBaseFromUrl(url, config));
		});
	}

	private getFileBase(url: string, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		return this.getFileBaseFromUrl(url, config).catch((error) => {
			return this.renewTokenIfIsExpiredOrLogout(error.response)
				.then(() => this.getFileBaseFromUrl(url, config));
		});
	}

	private getBase(url: string, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		return this.getBaseFromUrl(url, config).catch((error) => {
			return this.renewTokenIfIsExpiredOrLogout(error.response)
				.then(() => this.getBaseFromUrl(url, config));
		});
	}

	protected updateBase(url: string, data: any, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		return this.updateBaseFromUrl(url, data, config).catch((error) => {
			return this.renewTokenIfIsExpiredOrLogout(error.response)
				.then(() => this.updateBaseFromUrl(url, data, config));
		});
	}

	private createBaseFromUrl(url: string, data: any, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		let configFromParam = config;
		return Axios.post(url, data, this.setLanguageAndTokenToHeader(configFromParam));
	}

	private deleteBaseFromUrl(url: string, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		let configFromParam = config;
		return Axios.delete(url, this.setLanguageAndTokenToHeader(configFromParam));
	}

	private getFileBaseFromUrl(url: string, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		//VaP     - rychly bugfix dle techto for
		//        - https://www.codegrepper.com/code-examples/javascript/axios+download+excel+%5C+file
		//        - https://laptrinhx.com/how-to-download-excel-file-in-axios-999168532/
		config = config || {};
		config.responseType = "blob";
		let configFromParam = config;
		return Axios.get(url, this.setLanguageAndTokenToHeader(configFromParam));
	}

	private getBaseFromUrl(url: string, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		let configFromParam = config;
		return Axios.get(url, this.setLanguageAndTokenToHeader(configFromParam));
	}

	private updateBaseFromUrl(url: string, data: any, config?: AxiosRequestConfig | undefined): Promise<AxiosResponse<any>> {
		let configFromParam = config;
		return Axios.put(url, data, this.setLanguageAndTokenToHeader(configFromParam));
	}

	private setLanguageAndTokenToHeader(config?: AxiosRequestConfig): AxiosRequestConfig {
		var localizationString = getActualLocalizationString();
		config = config || {};
		if (localizationString) {
			config.headers = config.headers || {};
			config.headers['Accept-Language'] = localizationString;
		}
		return this.setTokenToHeader(config);
	}


	private setTokenToHeader(config?: AxiosRequestConfig): AxiosRequestConfig {
		var jwtToken = getJwtToken();
		config = config || {};
		if (jwtToken) {
			config.headers = config.headers || {};
			config.headers['Authorization'] = 'Bearer ' + jwtToken;
		}
		return config;
	}

	private isTokenExpired(response: AxiosResponse<any>) {
		if (response) {
			return response.status === 401 && response.headers['token-expired'];
		}
		return false;
	}

	private async renewTokenIfIsExpiredOrLogout(response: AxiosResponse<any>): Promise<boolean> {
		if (this.isTokenExpired(response)) {
			return await this.refreshTokenSync();
		}
		return Promise.reject(response);
	}

	private async refreshTokenSync(): Promise<boolean> {
		return await collectionMutex.dispatch(async () => {
			return this.refreshToken().then((response) => {
				return response;
			}).catch((error) => {
				logoutCurrentUser('Vypršela platnost vašeho přihlášení.');
				throw { message: 'Vypršela platnost vašeho přihlášení.', type: 'IsTokenExpired' };
			});
		});
	}

	private async refreshToken(): Promise<boolean> {
		// VaP - pokud se dela na strance nekolik axios requestu, tak se pro kazdy volal refreshToken a nastala jedna z variant
		//     1 Prvni volani udelalo spravne refresh tokenu, a ostatni popadali s chybou, jelikoz pouzili jiz vyuzity refresh token
		//     2 Vsecha volani provedla spravne refresh tokenu, ale tim padem se napr. na detailu uzivatele behem jednoho vyprseni tokenu provedl 4x refresh, coz je naprosto zbytecna zatez
		//     - nasledna kontrola, zajisti to, aby se provedl refreshToken pouze pokud byla naposledy volan vic jak minutu zpatky. Lepsi reseni jsem nevymyslel, ale aspon funguje.
		if (_lastTokenRefresh == 0 || tlIsBefore(tlAddMinutes(1, _lastTokenRefresh), Date.now()) ) {
			_lastTokenRefresh = Date.now();
			var jwtTokenData = {
				jwtToken: getJwtToken(),
				refreshToken: getRefreshToken()
			};
			return Axios.post('JWT/refresh', jwtTokenData).then(response => {
				this.processTokensFromData(response.data);

				return Promise.resolve(true);
			}).catch(() => {
				return Promise.reject(false)
			});
		}
		return Promise.resolve(true);
	}

	private processTokensFromData(data: any) {
		setTokens(data.jwtToken, data.refreshToken);
	}
}

// VaP - zdroj zde https://spin.atomicobject.com/2018/09/10/javascript-concurrency/
//     - v metodach renewTokenIfIsExpiredOrLogout a refreshToken
//     - duvod proc to pouzivam je ten, ze kdyz je na strance vic REST volani (napr. aktualne na detailu uzivatele, kde se natahuje seznam roli, historie prihlaseni, ciselnik a vlastni uzivatel)
//     - tak se v pripade vyprseni tokenu zavole refresh token pro kazde REST volani
//     - z toho duvodu uspesne projde pouze to prvni, ale ty dalsi jiz ne, jelikoz poslou stare tokeny na obnovu a proto dostanou zpatky 401 a dojde k odhlaseni uzivatele
//     - dalsi problem, ktere toto reseni resi je to, ze staci, aby refresh token zavolalo pouze prvni volani a ostatni pockaly, az se provede refresh tokenu
//     - k tomu slouozi _isRenew a _isFirst
class Mutex {
	private mutex = Promise.resolve();

	lock(): PromiseLike<() => void> {
		let begin: (unlock: () => void) => void = unlock => { };

		this.mutex = this.mutex.then(() => {
			return new Promise(begin);
		});

		return new Promise(res => {
			begin = res;
		});
	}

	async dispatch<T>(fn: (() => PromiseLike<T>)): Promise<T> {
		const unlock = await this.lock();
		try {
			return await Promise.resolve(fn());
		} finally {
			unlock();
		}
	}
}

const collectionMutex = new Mutex();
var _lastTokenRefresh: number = 0;
